/**
 * useTask  — fetches a single task by ID with realtime updates + local mutate.
 * useTasks — fetches all tasks visible to the current user with realtime
 *            updates applied INCREMENTALLY (no full refetch per event).
 *
 * Realtime strategy:
 *   - tasks INSERT  -> fetch just that row (with joins) and prepend it.
 *   - tasks UPDATE  -> merge payload.new over the cached row, preserving
 *                      the joined owner/creator objects (no query at all).
 *   - tasks DELETE  -> drop the row locally.
 *   - comments INSERT -> patch latest_comment from the payload (no query).
 *
 * Both hooks also expose local mutation helpers (upsertTask / patchTask /
 * mutate) so screens can update optimistically the moment a write succeeds,
 * instead of waiting a realtime round trip.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../api/supabase.js';

export const TASK_LIST_SELECT = `
  *,
  owner:users!owner_id(id, full_name, role, department, avatar_url),
  creator:users!created_by(id, full_name)
`;

const TASK_DETAIL_SELECT = `
  *,
  owner:users!owner_id(id, full_name, role, department, avatar_url),
  creator:users!created_by(id, full_name),
  assigner:users!assigned_by(id, full_name)
`;

/** Merge a raw realtime row over a cached row, keeping joined objects. */
export function mergeTaskRow(existing, incoming) {
  if (!existing) return incoming;
  return {
    ...existing,
    ...incoming,
    owner: incoming.owner ?? existing.owner,
    creator: incoming.creator ?? existing.creator,
    assigner: incoming.assigner ?? existing.assigner,
    latest_comment: incoming.latest_comment ?? existing.latest_comment ?? null,
  };
}

/** Insert-or-replace by id; new rows are prepended (created_at DESC order). */
export function upsertById(rows, row) {
  if (!row?.id) return rows;
  const idx = rows.findIndex((t) => t.id === row.id);
  if (idx === -1) return [row, ...rows];
  const next = rows.slice();
  next[idx] = mergeTaskRow(rows[idx], row);
  return next;
}

/** Batch-fetch the latest comment body per task (separate query — reliable across RLS/embed configs). */
async function fetchLatestCommentsByTask(taskIds) {
  const map = new Map();
  if (!taskIds.length) return map;

  const { data, error } = await supabase
    .from('comments')
    .select('task_id, body, created_at')
    .in('task_id', taskIds)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[ORIN] Failed to fetch latest comments:', error.message);
    return map;
  }

  for (const row of data || []) {
    if (!map.has(row.task_id)) map.set(row.task_id, row.body);
  }
  return map;
}

let channelSeq = 0;

export function useTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  const fetchIdRef = useRef(0);

  const fetchTasks = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setError(null);

    const { data, error: fetchError } = await supabase
      .from('tasks')
      .select(TASK_LIST_SELECT)
      .order('created_at', { ascending: false });

    if (!mountedRef.current || fetchId !== fetchIdRef.current) return;

    if (fetchError) {
      console.error('[ORIN] Failed to fetch tasks:', fetchError.message);
      setError(fetchError.message);
      setTasks([]);
      setLoading(false);
      return;
    }

    const rows = data || [];
    // Show tasks immediately; attach latest comments when they arrive
    // instead of blocking first paint behind a second query.
    setTasks(rows.map((t) => ({ ...t, latest_comment: t.latest_comment ?? null })));
    setLoading(false);

    if (rows.length) {
      const commentMap = await fetchLatestCommentsByTask(rows.map((t) => t.id));
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      if (commentMap.size) {
        setTasks((current) =>
          current.map((t) =>
            commentMap.has(t.id) ? { ...t, latest_comment: commentMap.get(t.id) } : t,
          ),
        );
      }
    }
  }, []);

  /** Optimistic insert/replace — call with the row returned by .insert().select(). */
  const upsertTask = useCallback((row) => {
    if (!row?.id) return;
    setTasks((current) => upsertById(current, row));
  }, []);

  /** Local field patch (e.g. status flip) without waiting for the server. */
  const patchTask = useCallback((id, patch) => {
    if (!id) return;
    setTasks((current) =>
      current.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    );
  }, []);

  /** Optimistic removal — realtime DELETE confirms (and is a no-op then). */
  const removeTask = useCallback((id) => {
    if (!id) return;
    setTasks((current) => current.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchTasks();

    const fetchOneAndUpsert = async (id) => {
      const { data } = await supabase
        .from('tasks')
        .select(TASK_LIST_SELECT)
        .eq('id', id)
        .maybeSingle();
      if (data && mountedRef.current) {
        setTasks((current) => upsertById(current, data));
      }
    };

    // Unique channel name — avoids topic collisions across remounts
    // (StrictMode) or multiple consumers of this hook.
    const channel = supabase
      .channel(`tasks-list-${++channelSeq}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'tasks' }, (payload) => {
        if (!mountedRef.current || !payload.new?.id) return;
        fetchOneAndUpsert(payload.new.id);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'tasks' }, (payload) => {
        if (!mountedRef.current || !payload.new?.id) return;
        setTasks((current) => {
          const idx = current.findIndex((t) => t.id === payload.new.id);
          if (idx === -1) return current; // not visible in this list
          const next = current.slice();
          next[idx] = mergeTaskRow(current[idx], payload.new);
          return next;
        });
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'tasks' }, (payload) => {
        if (!mountedRef.current || !payload.old?.id) return;
        setTasks((current) => current.filter((t) => t.id !== payload.old.id));
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'comments' }, (payload) => {
        if (!mountedRef.current) return;
        const { task_id: taskId, body } = payload.new || {};
        if (!taskId || body === undefined) return;
        setTasks((current) =>
          current.map((t) => (t.id === taskId ? { ...t, latest_comment: body } : t)),
        );
      })
      .subscribe();

    return () => {
      mountedRef.current = false;
      supabase.removeChannel(channel);
    };
  }, [fetchTasks]);

  return { tasks, loading, error, refresh: fetchTasks, upsertTask, patchTask, removeTask };
}

export function useTask(taskId) {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  const fetchIdRef = useRef(0);

  const fetchTask = useCallback(async () => {
    if (!taskId) {
      setTask(null);
      setLoading(false);
      return;
    }
    const fetchId = ++fetchIdRef.current;
    setError(null);

    const { data, error: fetchError } = await supabase
      .from('tasks')
      .select(TASK_DETAIL_SELECT)
      .eq('id', taskId)
      .maybeSingle();

    if (!mountedRef.current || fetchId !== fetchIdRef.current) return;

    if (fetchError) {
      console.error('[ORIN] Failed to fetch task:', fetchError.message);
      setError(fetchError.message);
      setTask(null);
    } else {
      setTask(data);
    }
    setLoading(false);
  }, [taskId]);

  /**
   * Optimistic local patch — the page applies status/timer changes the
   * instant the mutation succeeds; realtime then confirms with the
   * authoritative row (merged, so joins survive).
   */
  const mutate = useCallback((patch) => {
    if (!patch) return;
    setTask((current) => (current ? { ...current, ...patch } : current));
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchTask();

    if (!taskId) return undefined;

    const channel = supabase
      .channel(`task-${taskId}-${++channelSeq}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'tasks', filter: `id=eq.${taskId}` },
        (payload) => {
          if (!mountedRef.current || !payload.new) return;
          setTask((current) => mergeTaskRow(current, payload.new));
        },
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'tasks', filter: `id=eq.${taskId}` },
        () => {
          if (mountedRef.current) setTask(null);
        },
      )
      .subscribe();

    return () => {
      mountedRef.current = false;
      supabase.removeChannel(channel);
    };
  }, [taskId, fetchTask]);

  return { task, loading, error, refresh: fetchTask, mutate };
}
